import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSubscriptionProduct } from './services.js';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Currencies from '../../../constants/currencies';
import { setHeaderText } from '../../../features/headerText/headerTextSlice';
import '../../subscription/subscription.css';

const DURATION_OPTIONS = [
  { value: 'daily', label: 'Day' },
  { value: 'weekly', label: 'Week' },
  { value: 'monthly', label: 'Month' },
  { value: 'yearly', label: 'Year' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const AddPlan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.masterAuth.masterUser.token);
  const { appSettings } = useSelector((state) => state.appSettings);

  const [name, setName] = useState('');
  const [monthlyPricing, setMonthlyPricing] = useState('');
  const [yearlyPricing, setYearlyPricing] = useState('');
  const [features, setFeatures] = useState([]);
  const [currenciesList, setCurrenciesList] = useState([]);
  const [currencyCodeSearchTerm, setCurrencyCodeSearchTerm] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [trailPeriod, setTrailPeriods] = useState('7');
  const [duration, setDuration] = useState('monthly');
  const [status, setStatus] = useState('active');
  const [description, setDescription] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const currencyDropdownRef = useRef(null);
  const currencyCodeRef = useRef(null);
  const [currencyCodeDropdownOpen, setCurrencyCodeDropdownOpen] = useState(false);
  const durationDropdownRef = useRef(null);
  const [durationDropdownOpen, setDurationDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  useEffect(() => {
    const sorted = Currencies.sourceCurrencies[0].targetCurrencies
      .slice()
      .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));
    setCurrenciesList(sorted);
    dispatch(setHeaderText("Subscriptions"));

    const handleOutsideClick = (event) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
        setCurrencyCodeDropdownOpen(false);
      }
      if (durationDropdownRef.current && !durationDropdownRef.current.contains(event.target)) {
        setDurationDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [dispatch]);

  const filteredCurrency = currenciesList.filter((c) => {
    const label = `${c.currencyCode} (${c.currencyName})`;
    return label.toLowerCase().includes(currencyCodeSearchTerm.toLowerCase());
  });

  const handleFeatureChange = (e, index) => {
    const updated = [...features];
    updated[index] = e.target.value;
    setFeatures(updated);
  };

  const handleAddFeature = (e) => {
    e.preventDefault();
    setFeatures([...features, '']);
  };

  const handleDeleteFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!name.trim()) errors.name = "Plan name is required";
    if (monthlyPricing === '') errors.monthlyPricing = "Monthly pricing is required";
    if (yearlyPricing === '') errors.yearlyPricing = "Yearly pricing is required";
    if (!description.trim()) errors.description = "Description is required";
    if (features.length === 0) errors.features = "Add at least one plan feature";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    const response = await createSubscriptionProduct(
      name, description, currencyCode, trailPeriod,
      monthlyPricing, yearlyPricing, features, duration, status, token
    );
    if (response?.message === "Product already exists") {
      toast.error(response.message, { position: "bottom-right", autoClose: 2000 });
      return;
    }
    toast.success('Plan created successfully!', { position: "bottom-right", autoClose: 2000 });
    navigate("/master-user/subscriptions", { state: { fromEditOrAdd: true } });
  };

  const durationLabel = DURATION_OPTIONS.find(o => o.value === duration)?.label || 'Select Duration';
  const statusLabel = STATUS_OPTIONS.find(o => o.value === status)?.label || 'Select Status';

  const currencySymbol = (() => {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode || 'USD' })
        .formatToParts(1)
        .find(p => p.type === 'currency')?.value || '$';
    } catch {
      return '$';
    }
  })();

  return (
    <div className='plan-edit-outer'>
      <div className='plan-edit-top'>
        <div className='top-left-edit'>Create Plan</div>
        <div className='top-right-edit'>
          <button onClick={() => navigate("/master-user/subscriptions")}>
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 11.5L11.5 1" stroke="#282828" />
                <path d="M1 1L11.5 11.5" stroke="#282828" />
              </svg>
            </span>
            Cancel
          </button>
        </div>
      </div>

      <div className='plan-edit-inner'>
        <form onSubmit={e => e.preventDefault()}>

          {/* Row 1: Name + Pricing */}
          <div className='plan-edit-inner-top'>
            <div className='plan-edit-inner-top-left'>
              <label>
                Name:
                <input
                  type="text"
                  value={name}
                  onChange={e => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: '' })); }}
                  style={fieldErrors.name ? { border: '1.5px solid #e84c4c', backgroundColor: '#fff5f5' } : {}}
                />
                {fieldErrors.name && <span className="sub-field-error">{fieldErrors.name}</span>}
              </label>
            </div>
            <div className='plan-edit-inner-top-right'>
              <label>
                Monthly Pricing ({currencySymbol}):
                <input
                  type="number"
                  value={monthlyPricing}
                  onChange={e => { setMonthlyPricing(e.target.value); setFieldErrors(p => ({ ...p, monthlyPricing: '' })); }}
                  min="0"
                  style={fieldErrors.monthlyPricing ? { border: '1.5px solid #e84c4c', backgroundColor: '#fff5f5' } : {}}
                />
                {fieldErrors.monthlyPricing && <span className="sub-field-error">{fieldErrors.monthlyPricing}</span>}
              </label>
              <label>
                Yearly Pricing ({currencySymbol}):
                <input
                  type="number"
                  value={yearlyPricing}
                  onChange={e => { setYearlyPricing(e.target.value); setFieldErrors(p => ({ ...p, yearlyPricing: '' })); }}
                  min="0"
                  style={fieldErrors.yearlyPricing ? { border: '1.5px solid #e84c4c', backgroundColor: '#fff5f5' } : {}}
                />
                {fieldErrors.yearlyPricing && <span className="sub-field-error">{fieldErrors.yearlyPricing}</span>}
              </label>
            </div>
          </div>

          {/* Row 2: Currency + Trial/Duration/Status/Description */}
          <div className='plan-edit-inner-top'>
            <div className='plan-edit-inner-top-right'>

              <div className="form-field-client">
                <label htmlFor="currency" className="form-label">Currency</label>
                <div ref={currencyDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                  <span
                    id="currency"
                    onClick={() => setCurrencyCodeDropdownOpen(o => !o)}
                    className="custom-dropdown-user-new"
                    ref={currencyCodeRef}
                  >
                    {currencyCode ? `${currencyCode} (${currencySymbol})` : 'Select Currency'}
                  </span>
                  {currencyCodeDropdownOpen && (
                    <div className="custom-dropdown-new">
                      <input
                        type="text"
                        className="search-bar"
                        placeholder={global.config.locate(appSettings?.language, 'Search')}
                        value={currencyCodeSearchTerm}
                        onChange={e => setCurrencyCodeSearchTerm(e.target.value)}
                      />
                      <ul className="country-list">
                        {filteredCurrency.map((c) => (
                          <li
                            key={c.currencyCode}
                            className={currencyCode === c.currencyCode ? 'selected-item' : ''}
                            onClick={() => { setCurrencyCode(c.currencyCode); setCurrencyCodeDropdownOpen(false); }}
                          >
                            {`${c.currencyCode} (${c.currencyName})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-start' }}>

                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <div className="form-field-client">
                    <label className="form-label">Trial period (days)</label>
                    <input
                      min={0}
                      type="number"
                      value={trailPeriod}
                      onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        if (v >= 0 || e.target.value === '') setTrailPeriods(e.target.value);
                      }}
                      style={{ display: 'block', width: '100%', height: '40px', borderRadius: '5px', border: '0.5px solid #B4B4B4', padding: '7px 15px', fontSize: '17px', fontWeight: '300', color: '#282828', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <div className="form-field-client">
                    <label className="form-label">Duration</label>
                    <div ref={durationDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                      <span onClick={() => setDurationDropdownOpen(o => !o)} className="custom-dropdown-user-new">
                        {durationLabel}
                      </span>
                      {durationDropdownOpen && (
                        <div className="custom-dropdown-new">
                          <ul className="country-list">
                            {DURATION_OPTIONS.map(opt => (
                              <li
                                key={opt.value}
                                className={duration === opt.value ? 'selected-item' : ''}
                                onClick={() => { setDuration(opt.value); setDurationDropdownOpen(false); }}
                              >
                                {opt.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <div className="form-field-client">
                    <label className="form-label">Status</label>
                    <div ref={statusDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                      <span onClick={() => setStatusDropdownOpen(o => !o)} className="custom-dropdown-user-new">
                        {statusLabel}
                      </span>
                      {statusDropdownOpen && (
                        <div className="custom-dropdown-new">
                          <ul className="country-list">
                            {STATUS_OPTIONS.map(opt => (
                              <li
                                key={opt.value}
                                className={status === opt.value ? 'selected-item' : ''}
                                onClick={() => { setStatus(opt.value); setStatusDropdownOpen(false); }}
                              >
                                {opt.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Row 3: Description */}
          <div className='plan-edit-inner-top'>
            <div className='plan-edit-inner-top-right' style={{ width: '100%' }}>
              <div className="form-field-client" style={{ width: '100%' }}>
                <label className="form-label">Description</label>
                <textarea
                  value={description}
                  onChange={e => { setDescription(e.target.value); setFieldErrors(p => ({ ...p, description: '' })); }}
                  rows={4}
                  style={{ width: '100%', resize: 'vertical', borderRadius: '5px', border: fieldErrors.description ? '1.5px solid #e84c4c' : '0.5px solid #B4B4B4', backgroundColor: fieldErrors.description ? '#fff5f5' : '', padding: '10px 15px', fontSize: '17px', fontWeight: '300', color: '#282828', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
                {fieldErrors.description && <span className="sub-field-error">{fieldErrors.description}</span>}
              </div>
            </div>
          </div>

          {/* Features */}
          <div className='feature-edit-subscription'>
            <div className='feature-edit-title'>Plan features</div>
            <ul>
              {features.map((feature, index) => (
                <li key={index}>
                  <input
                    type="text"
                    value={feature}
                    onChange={e => handleFeatureChange(e, index)}
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteFeature(index)}
                    style={{ marginLeft: '10px', color: 'red' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none">
                      <path d="M16.9577 19.6445L17.4363 4.37763H19.5772V3.85209H14.0109V2.45942C14.0109 1.69739 13.4316 1.09302 12.7011 1.09302H7.966C7.23557 1.09302 6.65628 1.69739 6.65628 2.45942V3.85209H1.08997V4.37763H3.23085L3.70941 19.6445C3.73459 20.354 4.2887 20.9058 4.96875 20.9058H15.6732C16.3784 20.9058 16.9325 20.354 16.9577 19.6445ZM7.16001 2.45942C7.16001 1.98643 7.51263 1.61856 7.966 1.61856H12.7011C13.1545 1.61856 13.5071 1.98643 13.5071 2.45942V3.85209H7.16001V2.45942ZM4.21314 19.6182L3.73459 4.37763H16.9325L16.454 19.6182C16.4288 20.0387 16.1014 20.3803 15.6984 20.3803H4.96875C4.56576 20.3803 4.23833 20.0387 4.21314 19.6182Z" fill="#405BFF" stroke="#282828" strokeWidth="0.5" />
                      <path d="M15.6736 21H4.96874C4.23877 21 3.64643 20.4064 3.61944 19.6486L3.14359 4.4723H1V3.75907H6.56631V2.46024C6.56631 1.64144 7.18114 1 7.96598 1H12.7011C13.486 1 14.1008 1.64144 14.1008 2.46024V3.75907H19.6671V4.4723H17.5231L17.0504 19.5515H17.0508L17.0472 19.6486C17.0202 20.4064 16.4166 21 15.6732 21H15.6736Z" fill="#405BFF" stroke="#282828" strokeWidth="0.5" />
                      <path d="M10.0811 8.3717H10.5848V16.2285H10.0811V8.3717Z" fill="#405BFF" stroke="#282828" strokeWidth="0.5" />
                      <path d="M7.05884 8.3717H7.56258V16.2285H7.05884V8.3717Z" fill="#405BFF" stroke="#282828" strokeWidth="0.5" />
                      <path d="M13.1046 8.3717H13.6084V16.2285H13.1046V8.3717Z" fill="#405BFF" stroke="#282828" strokeWidth="0.5" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
            <button className="add-feature" onClick={e => { handleAddFeature(e); setFieldErrors(p => ({ ...p, features: '' })); }}>
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M6.75 0V13.5" stroke="#282828" />
                  <path d="M13.5 6.75L0 6.75" stroke="#282828" />
                </svg>
              </span>
              Add Item
            </button>
            {fieldErrors.features && <span className="sub-field-error">{fieldErrors.features}</span>}
          </div>

        </form>
      </div>

      <div className="plan-edit-inner-bottom">
        <button onClick={handleSave} className="edit-save">Create Plan</button>
      </div>
    </div>
  );
};

export default AddPlan;
